import { CheckboxField } from '@flos/react-ui';
import { useEffect, useState } from 'react';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { SummaryRowTemplate } from '../../helpers/templates';
import { getPrimaryEmail, getPrimaryPhone } from '../../helpers/topContext';
import useAppState from '../../state/appState';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { ElementValue, ElementValueHuman } from '../../types/element-values';
import {
    ContactUpdateProfile as ContactUpdateProfileType,
    FormConfig,
    FormValues
} from '@topdanmark-onlinesolutions/claim-form-types';
import { getIsValid } from '../../validators/validator-mapping';
import { getValue } from '../values/Get';

const ContactUpdateProfile = ({ caption, elementId, listenOnElementId }: ContactUpdateProfileType) => {
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const value = ContactUpdateProfile.getValueOrDefault(formValues, elementId);

    const [isVisible, setIsVisible] = useState(false);

    const _contacts = JSON.stringify(listenOnElementId.map((elementId) => formValues[elementId])); //prevent infinite loop on re-rendering

    useEffect(() => {
        const contacts = JSON.parse(_contacts);
        const topContextEmail = getPrimaryEmail();
        const topContextPhone = getPrimaryPhone();

        const topContextContact = [topContextEmail, topContextPhone];
        const containsContact = topContextContact.every((contact) => contacts.includes(contact));

        if (containsContact) {
            //either phone/email match with topContext
            setIsVisible(false);
            setFormValue(elementId, false);
        } else {
            if (contacts.includes(null)) {
                //do not display checkbox if phone/email is undefined in topContext.
                //JSON.parse converts to 'null'
                setIsVisible(false);
            } else {
                //do not display checkbox if phone/email is empty
                setIsVisible(!contacts.includes(''));
            }
            setFormValue(elementId, true);
        }
    }, [_contacts, setIsVisible, setFormValue, elementId]);

    const setValue = () => {
        setFormValue(elementId, value === false ? true : false);
    };

    return (
        <>
            {isVisible && (
                <CheckboxField
                    value={value}
                    label={caption}
                    checked={value}
                    onClick={setValue}
                    id={elementId}
                    key={elementId}
                    aria-label={caption}
                    data-testid="contact-update"
                />
            )}
        </>
    );
};

ContactUpdateProfile.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as ContactUpdateProfileType;
    const { summaryCaption } = element;
    const value = getValue(formConfig, formValues, elementId);
    if (value === undefined) {
        return <></>;
    }

    const label = value ? 'Ja' : 'Nej';

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={label} />;
};

ContactUpdateProfile.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    caption: string
): ClaimFormSubmitFieldValue | undefined => {
    // This contact information is handled in a separate object in the payload Stakeholder and Actions
    return undefined;
};

ContactUpdateProfile.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    const value = formValues[elementId];
    return value;
};

ContactUpdateProfile.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = ContactUpdateProfile.getValue(formValues, elementId);

    if (value === undefined) {
        return true;
    }
    return value;
};

ContactUpdateProfile.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    return getIsValid(formConfig, formValues, elementId);
};

ContactUpdateProfile.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const value = ContactUpdateProfile.getValue(formValues, elementId);

    return value ? 'Ja' : 'Nej';
};

export default ContactUpdateProfile;
